// Help pages, one per screen, Studio step and account panel, and a few for what cuts across them (docs/19, ADR 0026).
//
// The rule these are written to: describe the product as it is on the day it ships, never as it is designed. Where a
// screen promises more than the product does, the page says what is true and lists the rest under Not yet, and the
// screen's sentence is an open item in docs/14. A screen that changes without its page changing is an unfinished change.
// These pages are also the only thing a help assistant will ever be allowed to answer from, so a sentence here is a
// sentence it may repeat to a customer. No internal document is named in them, and no margin is published.
import { GLOSSARY } from "./glossary.js";
import type { HelpPage } from "./types.js";

export const HELP_PAGES: HelpPage[] = [
  {
    slug: "getting-started",
    title: "Getting started",
    where: null,
    summary: "From signing in to your first video or post, in the order the app expects.",
    sections: [
      {
        heading: "Sign in",
        body: [
          "Enter your email and press Send me a link. The link works once and expires in 15 minutes. There is no password.",
          "If a Continue with Google button is shown, you can use your Google account instead.",
          "Anyone can sign up: the first sign-in with an address makes its account. The screen says a link is on its way if the address can sign in, because a server can be kept to a list of addresses, and one not on it gets no email.",
        ],
      },
      {
        heading: "Your first workspace",
        body: [
          "Your first sign-in makes a workspace for your business. It starts with no credit: Joogoos is paid for as it is used, so the first thing to do is add some in Credits, from $10. Until you do, every tab shows Add credit to start, with the amounts to choose from. If you run out later, a strip across the top says so, and everything you made stays where it is.",
          "The Business lookup, and everything the Studio makes, is charged per call and listed in the Credits panel. The Studio shows a post's price before it is made; the lookup's is listed once it has run. Nothing is taken monthly and there is no subscription.",
        ],
      },
      {
        heading: "Tell it about the business",
        body: [
          "The Business tab asks one thing: where to find the business. Give whatever you have — the name and the town, a website, an Instagram page — or press It's an idea and describe it. Joogoos looks the business up, writes a first draft of one page about it, and you check it. The page is sent with every brief, so the more of it is right, the better everything the Studio makes.",
        ],
      },
      {
        heading: "Make something",
        body: [],
        list: [
          "Open the Studio and say in a sentence what you want to make.",
          "Answer the questions: a post or a video, where it will go, which photos, and whether it is narrated.",
          "Read the plan: each step, and about what it costs.",
          "Press Generate. The script and the shots take a minute or two, and you can leave and come back.",
          "When the video is ready, press Download, or Post now or Schedule to put it on an account connected under Business → Channels.",
        ],
      },
      {
        heading: "Tell us",
        body: ["Help, under your picture in Settings, is where you ask how something works, or send us a problem, an idea or a question with the screen you are on. A person reads every report."],
      },
      {
        heading: "On a phone",
        body: [
          "The tabs are along the bottom of the screen. In the Studio and in Business the chat comes first; the plan, the video or the business page is one tap away at the top, and a dot on it means something there has changed. Your posts open from the list button beside the title. The Calendar shows its list.",
        ],
      },
    ],
    notYet: [
      "Posting to a platform this box has no keys for: its row under Business → Channels says not on this box yet. Download the file and post it yourself.",
      "Buying credit on a phone leaves the app for the payment page, like it does on a desk, and comes back to Credits.",
    ],
    related: ["business", "channels", "studio", "credits", "reports"],
  },
  {
    slug: "overview",
    title: "Overview",
    where: "tab:Overview",
    summary: "The first tab: your marketing review, what needs you, what waits for your yes, how the marketing is going, and what each of the other tabs does.",
    sections: [
      {
        heading: "Your marketing review",
        body: [
          "Review my marketing, at the top of the Overview, reads your last seven days: who came to your website and who got in touch, the companies Customers found and the letters it wrote and sent, and the posts that went out or stopped. Beside them it reads the searches Google Search Console last reported, which are Google's own last four weeks rather than this week's, and what you decided after earlier reviews. Reading those costs nothing. Writing the review is one paid call to the assistant your workspace uses for the Studio, usually a few cents, and the price is on the button before you press it. Anyone in the workspace can ask for one, up to three a day, and a new one waits until the last recommendation is decided.",
          "It says what happened, what it cannot be sure of, and what Joogoos cannot see, with a link to where each one is fixed when it can be fixed from here: a website that is not being counted looks a lot like a quiet week, so it says which one it is. Then it recommends one thing to do, with why, what it costs, about how much of your time it takes, how it will be counted and when it will be looked at again, and up to two other options. When the week holds too little to learn from, the recommendation is marked A small test: a few posts or letters over a few weeks, never a promise of what will work.",
          "Only the owner of the business decides: Approve, Change, or Not this, with a reason if you want to give one. Approving spends nothing, sends nothing and publishes nothing. It opens the work where it is done: a post waiting for you in the Studio, or the tab where the thing the review could not see is fixed. Anything there that costs money shows its own price on its own button, as it always does. Mark it done says you finished it yourself.",
          "On the day it said, Joogoos counts the same thing again and says what changed after you said yes. It says after, not because: other things happen in the same weeks, and it names the other changes it knows were running. The next review is told what you decided and what came of it, so it does not suggest again what you turned down.",
          "Under the review you can ask about it, or say what you would do instead. Each question is a small paid call, priced on Send. An answer only uses figures the review counted, and when it suggests a change, Use this puts the change on the recommendation for you to approve. Nothing changes until you press it.",
        ],
      },
      {
        heading: "What needs you",
        body: [
          "Next lists what is waiting: a review recommendation waiting for your decision, if you own the business, a business page the Studio cannot write from yet, a draft nobody came back to, work that stopped at its cap, a website that is not being counted, and credit about to run out. Each row opens the tab that fixes it.",
          "Nothing on this page is a number of its own. Everything on it is read from the tab it points at, so the two can never disagree.",
        ],
      },
      {
        heading: "Waiting for your yes",
        body: [
          "Under what needs you, Waiting for your yes gathers everything Joogoos has prepared that cannot go anywhere without you: letters to companies waiting to be sent, posts waiting to be approved, posts and videos you made that have not gone out, articles to read, articles approved and not yet on your website, topics to keep or drop, changes the Customers loop proposes, and companies that answered your letters. Your marketing review's recommendation is not on it, because it is already at the top of the page.",
          "Each row says what it is and what a yes would do, and the buttons are the same ones its own tab has: Send sends the letter exactly as Send on Customers does, Approve approves the post for the time it was planned for, or for about two minutes from now if that time has passed, Keep it and Drop it decide a topic. Nothing goes out until you press. A refusal says why in the same words its tab would use. An article is never approved from the list, because it is read first: Read it opens it on SEO. Sending a letter, approving a post and deciding a proposal are the owner's; anyone else sees Waits for the owner.",
          "Before the press, a letter says when a yes will not reach the company. While this server is rehearsing, the press is called Rehearse: the letter is delivered to your own address instead, and the company then counts as written to, so it is never written to again. While sending is switched off for your business, an approved letter waits until you turn it on.",
          "In the morning, the owners are emailed the list when something new has arrived on it since the last email: how many of each, the first few by name, and a button to the list. Nothing is answered by email. Stop the morning email, under the list, turns it off for this business, and Email it to me in the morning turns it back on.",
          "Under the list is what Joogoos may spend each week preparing it while you sleep. It starts at nothing, and nothing is prepared until the owner sets an amount, up to $100 a week, with Let Joogoos prepare it; Change it moves it, and 0 stops it. Once there is an amount, the owner can also tick the box to have a marketing review written every Monday out of it; it waits on this list for a yes, and no new one is written while the last waits. Within that amount, each night Joogoos drafts a few posts from your business page for the networks you are on, when fewer than three are waiting, and, if your website takes articles, plans topics when few are waiting and writes the next topic you kept. Each one lands here as a draft for your yes; nothing is sent, posted or published by it. It is paid from your credit like anything else, it never starts something the rest of the week could not cover, and the line says what it has spent this week. When your marketing review's latest recommendation you said yes to was a post or an article, that comes first, and the posts are about it. Letters are not part of it: Customers prepares those within its own weekly amount. Anyone can see the amount; only the owner can change it.",
        ],
      },
      {
        heading: "Before you have made anything",
        body: [
          "Until something has been made, the Overview is the explanation instead: what Joogoos is, the five steps of the work, and one line on each tab saying what it holds today and what it does not hold yet.",
          "Once you have made something, those words move under How this works, at the bottom of the page.",
        ],
      },
      {
        heading: "What Joogoos does",
        body: [
          "Joogoos is the whole of a small business's marketing rather than one tool in it: it keeps a page about your business and writes everything from it, makes the posts and the videos, writes the articles that get your own website found, counts the people it brings and what they came for, and goes looking for customers who have not heard of you.",
          "A finished post goes out at your word, Post now or Schedule, to the accounts connected under Business → Channels, or you download the file and put it up yourself. A platform this box has no keys for says not on this box yet. Two parts are still drawn rather than built, and say so on their own tabs: music, caption styles and overlays in Content, and writing a plan on a Calendar day.",
        ],
        list: [
          "Business — what Joogoos knows about you, and where your website, listing and channels are named.",
          "Studio — where a post or a video is made, with the price before it runs.",
          "Content — everything you have made, and the photos and clips it is made of. Music, caption styles and overlays are not built yet.",
          "Calendar — what is scheduled, by month, week or list. A post is scheduled from the Studio; writing a plan on a day is not built yet.",
          "Analytics — who came to your website, where from, and what to make next.",
          "SEO — blog articles written from your own sources and published on your own website.",
          "Customers — finding this business its next customer, and the letters that go out from your own address.",
        ],
      },
    ],
    notYet: [
      "A review written for you every week without pressing the button.",
      "Talking the review through by voice.",
      "How a post did on each network, and what happens after somebody gets in touch, such as a sign-up or an order in your own system: the review says it cannot see them.",
    ],
    related: ["getting-started", "business", "studio", "analytics", "credits"],
  },
  {
    slug: "business",
    title: "Business",
    where: "tab:Business",
    summary: "Your business, step by step: the page every brief is written from, your look and photos, your website, channels, customers and SEO.",
    sections: [
      {
        heading: "Setting it up",
        body: [
          "Business has a menu down the left (along the top on a phone): Where to find you, About the business, Your look, Photos, Website & analytics, Channels, Customers and SEO. While you set up, it is your checklist: each step shows a tick when it is done, and Next at the bottom of each screen takes you to the next one not done yet. Skip for now leaves a step for later, and the menu says so. Afterwards the same menu is how you get anywhere on Business.",
          "Where to find you is one field. Type whatever you have: the name and the town, a website, or an Instagram or Facebook page. If there is nothing online yet, the name and the town are enough. Read it starts the lookup. If the business has not opened yet, press It's an idea and describe it in a sentence or two instead; the page then says it is not open yet, opening hours and reviews are not counted, and Google is not searched for it, because nobody is on it before they open.",
          "Read it opens About the business, which fills in while the lookup reads. Beside it, the lookup's steps, and the one thing no website says: what do you want more of? Tap any of the answers. What you tap is yours, and the lookup never writes over it. You can leave the screen; the draft waits on Business.",
          "Website & analytics asks for your website address and gives you one line to paste into your site; from then on Analytics counts who visits. Once it is counting, the same step says what counts as getting in touch: a press on your phone number, email address or a WhatsApp link is counted by itself, and a form or any other button is counted once your website marks it with data-joogoos-goal and a short name of your own, such as <form data-joogoos-goal=\"call_request\">. A button counts when it is pressed and a form when it is sent. The name is written in small letters, digits, dashes or underscores, starting with a letter. Customers asks for the mailbox letters leave from and where to look for customers, and can draft who to write to. SEO asks who your articles are for and what they must never name, talk about or claim; it waits, marked needs a website, until there is one.",
        ],
      },
      {
        heading: "The lookup",
        body: [
          "Joogoos looks for the business on Google, reads its website if there is one (the homepage and up to three more pages), looks at who else nearby does the same thing, and works out what the business does from all of it, including what its Google reviews say. Then it gathers pictures from your website, the photos your business posted on its Google listing, and pictures of the business found online, such as on its Facebook page, with your logo and your colours. Each step shows one line when it finishes. Changing the name on the page starts a lookup again.",
          "With no credit the lookup does not start, and the first screen says so: add credit in Credits, then press Read it again. If the credit runs out part way, it stops and About the business says the same.",
          "The lookup makes paid calls, and no price is shown before it starts. Each appears afterwards in Credits, under Every call it has paid for, as lookup place, fetch page, find pages, pick pages, find competitors, read site, place photo and find images.",
        ],
      },
      {
        heading: "Checking what it read",
        body: [
          "When the lookup finishes, the page arrives written, and each line says where it came from: from Google, from your website, or our guess. Press Right on a line that is right, which makes it yours, words unchanged, or Edit to change it. All the rest is right does every remaining line at once.",
          "A line the lookup could only guess comes with a few answers to tap, and Type it for your own words; tap any that fit and press Save. How we sound shows the same post written three ways: pick the one that sounds like you.",
          "A business with nothing online goes through the same screen. Every line is then a guess from the name and the town, to tap true in about a minute, and the screen says you do not need a website to start. Show me lists places to make one: Instagram, TikTok or a Facebook page to start, Google Business Profile for the map, and Wix or Shopify for a website. They are plain links that open in a new tab, and nothing about you is sent to them.",
          "If the nearest business on Google has a different name or is in another town, nothing from it is written on the page, and the screen asks Is this you on Google? Yes, that's us looks that listing up again as yours; No drops the question.",
          "Anything you do not fix now can be fixed later. Next moves on to the next step not done yet.",
        ],
      },
      {
        heading: "Choosing your pictures, logo and colours",
        body: [
          "When the lookup finds pictures, Your look opens with them grouped by where they came from: From your website, From your Google listing and Found online. Your website's and your Google listing's pictures start ticked. Pictures found online do not, because they may not be yours to use. Tap a picture to tick or untick it.",
          "Pick your logo, or No logo, and a colour theme: From your logo, From your website, Bold or Deep. Every theme keeps white words readable, because videos put white words on those colours. The preview beside them shows a photo with its caption and the last frame of a video in those colours, and says so if your logo would not stand out. Tap a colour to fine-tune it, or × to remove it, up to three.",
          "Use this look puts the ticked pictures on the Studio's photo step, under From your lookup, sets your logo and colours for every video, and deletes the rest. None of these keeps nothing. Choosing costs nothing; each kept picture is described once, like an upload, and that is a paid call listed in Credits as describe image.",
          "Afterwards Your look is in the menu: Upload a logo (PNG, JPEG, WebP or SVG), Remove the logo, or try another theme and Save these colours. That costs nothing.",
          "Photos, under Your look in the menu, shows every picture a post can be made with, grouped Uploaded by you, From your lookup and From your posts. Upload adds more (PNG, JPEG or WebP); each is described once, a paid call listed in Credits as describe image. Your logo is not among them: it is on Your look.",
        ],
      },
      {
        heading: "The page",
        body: [
          "About the business is the business as Joogoos understands it, in two columns: on the left what the Studio writes from, on the right what the lookup found. See exactly what the Studio reads shows the words that are sent with every brief.",
          "Press Edit on a line to change it yourself, which costs nothing. What you save is yours.",
          "If the business was set up as an idea, We've opened on Where to find you says it has opened. That costs nothing too.",
          "Where you look for customers can also be changed on Customers, under Set up; who your articles are for and the never lists on SEO, under Your website. They are the same settings in both places.",
        ],
        list: [
          "The business: its name and its town.",
          "What we sell, and what you are best known for.",
          "Who buys it.",
          "What customers say: what your Google reviews praise, and anything they complain about.",
          "Who else they could choose: the businesses nearby doing the same thing, and what makes you different.",
          "How we sound: how your posts and videos should sound.",
          "What we want more of: what you most want to sell more of.",
          "Where to find us: the address, or that you work from a van, from home or online.",
          "When we are open.",
          "Where we are online: your website and social handles.",
        ],
      },
      {
        heading: "Why it matters",
        body: [
          "This page is sent with every brief. Nothing else about the business is: if it is not written here, the Studio does not know it.",
          "Until the business, what you sell, who buys it, how you sound and what you want more of are answered, Joogoos opens on the Overview, and the Studio shows a reminder with a Fill it in button.",
        ],
      },
    ],
    notYet: [
      "The pictures you keep are on the Studio's photo step, under From your lookup, and not in Content.",
    ],
    related: ["getting-started", "channels", "studio", "credits"],
  },
  {
    slug: "channels",
    title: "Channels",
    where: null,
    summary: "Connect an account once, under Business → Channels, and a finished post goes out on it at the time you chose.",
    sections: [
      {
        heading: "Where it is",
        body: [
          "Ask for reviews, also in the Business menu, gives you one link and a QR code to your own Google review form, and a message to copy. It needs a Google listing linked to your business. Joogoos sends nothing itself. Ask everyone and offer nothing for a review: Google removes reviews that were rewarded.",
          "Channels is a step in the Business tab's menu. It is there rather than in Settings or the Calendar because which Instagram this business posts as is a fact about the business, like its address.",
          "Your website and your Google listing are under Website & analytics, one step above it.",
        ],
      },
      {
        heading: "Connecting an account",
        body: [
          "Six accounts are always listed: Instagram, Facebook, TikTok, YouTube, LinkedIn and X. Press Connect and the platform's own site asks you to sign in and to allow posting, nothing else. When you come back the row shows the account's name and says posts can go out on it.",
          "Only an owner of the workspace can connect, pause or disconnect an account. Someone invited to draft posts has not been handed the business's public voice.",
          "One account per platform per workspace. Connecting again replaces the account and keeps whatever was waiting to go out.",
        ],
        list: [
          "Instagram needs a Business or Creator account; it refuses to publish from a personal one.",
          "Facebook needs a Page you administer; posts go to the Page, never to your own timeline.",
          "TikTok takes video only, and asks for the privacy setting of every post, because TikTok requires that to be your choice each time.",
          "YouTube needs a channel on the Google account you connect; a vertical video under a minute is published as a Short.",
          "LinkedIn needs nothing but the sign-in; posts go out as you, on your own profile.",
          "X needs a developer app on a paid X plan, because X charges for the right to post through it.",
        ],
      },
      {
        heading: "Not on this box yet",
        body: [
          "A platform whose row says not on this box yet cannot be connected here, because Joogoos has not been given its keys. Ask, or wait: the row will offer Connect when it has them.",
        ],
      },
      {
        heading: "Posting",
        body: [
          "A finished post in the Studio has Schedule and Post now. Both open the same dialog: what it says, which of your connected accounts it goes to, and, for Schedule, when. Post now means about a minute, and you can take it back until then.",
          "A post is one row per account. An account the platform would refuse for this post, a picture offered to TikTok or a caption too long for X, is shown with the reason and cannot be ticked.",
          "Nothing goes out without a person pressing that button. Publishing costs nothing and is not a paid call.",
        ],
      },
      {
        heading: "Pausing and disconnecting",
        body: [
          "Pause keeps the account connected and stops posts going out on it. Disconnect asks once, then gives the permission back to the platform before the account is forgotten. Posts that already went out stay on the Calendar under the platform's name.",
          "When a platform stops accepting the connection, the row says so once, with what the platform said and a Connect again button. Nothing goes out on it until you do.",
        ],
      },
    ],
    notYet: [
      "Numbers from your accounts in Analytics.",
      "More than one account on the same platform.",
      "Pinterest, Threads or any platform not in the six.",
    ],
    related: ["business", "studio-making", "calendar", "analytics", "not-yet"],
  },
  {
    slug: "studio",
    title: "Studio",
    where: "tab:Studio",
    summary: "Say what you want to make in your own words; Joogoos suggests the rest, you choose the photos, and nothing is made until you press Generate.",
    sections: [
      {
        heading: "Starting",
        body: [
          "Type a sentence under What are we making today? and send it. Or pick one of the eight ideas in the gallery on the right-hand side, which sets whether it is a post or a video and its shape for you. On a phone the gallery is under the chat box.",
          "Your posts are listed on the left: waiting for you, today, this week, and a link to older ones. The list stays open when you start a new post; collapse it with the arrow at its foot, and it stays collapsed until you open it again.",
          "New post opens your empty draft if you already have one, rather than starting another. A draft that was never made and has no photos chosen can be deleted: point at it in the list, press the bin, then Delete. Its messages go with it, and what they were charged stays in Credits.",
          "The chat stays in the same place throughout. The right-hand side changes as you go: the gallery, then your photos, a voice, the plan, and the video being made.",
        ],
      },
      {
        heading: "The conversation",
        body: [
          "What you type goes to the assistant chosen under the chat box. It works out what you mean, suggests what it can from your idea and your business page, and asks about what is still missing, usually the photos. You can answer several things in one message, or change your mind in the next.",
          "Its suggestions are listed under the conversation, after Suggested. Press one to take it back, and the question comes back with its buttons.",
          "It never chooses your photos and never presses Generate. Words it puts on screen with a price, a time, a phone number or a website come only from what you typed or from your business page.",
          "Each message you type is a paid call, listed in Credits as studio turn. The buttons in the chat and on the right cost nothing, and they are enough to finish on their own.",
        ],
        list: [
          "What it is about. One sentence is enough.",
          "A post, which is one image, or a short video.",
          "Where it will go, which sets the shape: Story 9:16, Square, Portrait 4:5 or Landscape.",
          "Which photos to use. Tick them on the right, or press None, invent them and pictures are generated.",
          "Whether it is narrated, and by whom. Use a voice on the right, or press No narration, just the words on screen.",
        ],
      },
      {
        heading: "Photos",
        body: [
          "Your photos are the ones you uploaded. From your lookup are the pictures you kept when the Business lookup found them. From your posts holds pictures from finished posts.",
          "Choosing a photo costs nothing. Uploading one does: each new photo is described once, so the script can use it, and that is a paid call listed in Credits.",
        ],
      },
      {
        heading: "Voices",
        body: ["There are six voices: Isla, Ruth, Theo, Ada, Mo and June, each with its accent and a word on how it sounds. Use picks one for the narration. You hear it in the finished video."],
      },
      {
        heading: "Prepare my week",
        body: [
          "Prepare my week, at the top of the list on the left, plans a post a day for the week ahead from your business page and your photos: five from tomorrow, at most one of them a video. Planning is one paid call of a few cents, listed in Credits as plan week, and its price is under the button. Nothing is made yet.",
          "The days are listed under Your week, each with its price. Open one to read why it was planned, change anything in the chat, and press Generate, as with any post. Drop a day with the cross beside it.",
          "Make all makes every day of the week at once, for the price on the button, after asking once. Each is charged as it is made. Nothing is posted: a finished post is scheduled like any other, and Schedule starts on the day it was planned for.",
          "A new week can be planned once the days of this one are made or dropped.",
        ],
      },
    ],
    notYet: [
      "Hearing a voice before you choose it.",
      "Find one, on the photo step, does not find pictures yet. Your own photos and generated ones both work.",
      "Attach, Photos and Dictate, under the chat box, do nothing yet.",
    ],
    related: ["studio-plan", "studio-making", "business", "credits"],
  },
  {
    slug: "studio-plan",
    title: "The plan and Generate",
    where: "step:plan",
    summary: "What will be made, what each step costs, and what happens when you press Generate.",
    sections: [
      {
        heading: "Reading the plan",
        body: [
          "When the questions are answered, the plan lists each step with a price. For a video: the script, the pictures, the clips, the narration, the music and the render. For a post: the words, one picture and laying out the words on the picture. A post has no music.",
          "A narrated video shows what the voice says as captions along the bottom, except in the Showreel, Editorial, Poster and Handmade styles, which have no captions: the big words on screen are what the voice says. A video with no narration has no captions in any style, and the words on screen carry it.",
          "The music is composed for each video and costs nothing. Turn it off beside the plan if the video should be quiet.",
          "The total says About, because what a step is charged depends on what it actually uses. Your credit left is shown beside it, and Generate carries the same total.",
        ],
      },
      {
        heading: "Before you press Generate",
        body: [
          "You can still change anything by saying it in the chat, such as another shape, another voice or words on screen, and the plan and its price change with it. Each message is a paid call, like the rest of the conversation.",
          "Anything under Suggested was chosen for you. Press one to take it back for nothing, and choose it yourself.",
        ],
      },
      {
        heading: "Generate",
        body: [
          "Generate writes the script first, then makes the shots. It takes a minute or two, and you can leave and come back.",
          "Once the script is written, the project gets a cap of about twice what its shots are expected to cost. A project that reaches its cap stops, and the list of posts says stopped at its cap.",
        ],
      },
      {
        heading: "When credit or a limit is in the way",
        body: [
          "The plan checks your credit and both your limits before Generate, so you find out before anything runs rather than half way through. The button then says at your limit, and pressing it says which one and offers to raise it — one field and one press — or to top up when it is the credit that has run out.",
          "If a step is refused after it has started, the post stops where it is with what was made, and the pipeline says why. A post that reached its own cap has a button there that raises it so the rest can be run. Nothing is lost either way, and everything free goes on working while you decide.",
          "Every refusal is also at the top of the call list in Credits.",
        ],
      },
    ],
    notYet: [
      "The prices on the plan come from a fixed table, not from the prices calls are charged at, so the two can differ.",
      "Running a step again, other than a shot.",
    ],
    related: ["studio", "studio-making", "credits"],
  },
  {
    slug: "studio-making",
    title: "Making it: Pipeline, Editor and Download",
    where: "step:making",
    summary: "Watching a project being made, rerunning a shot, and taking the finished video.",
    sections: [
      {
        heading: "Pipeline",
        body: [
          "Pipeline shows each step of the project as a box, done, running or queued, with what it cost or what is held while it runs. A red dot is a step that failed.",
          "The shape comes from the script and cannot be changed. Anything already sent to be made finishes, and is charged.",
        ],
      },
      {
        heading: "Rerunning a shot",
        body: [
          "Select a shot's step and press Rerun. A picture a model made, or changed from your photo, is made again, from what you typed beside Rerun in the Editor if you typed anything, with its clip if it moves and its narration if it has any. Then the finished video follows.",
          "A drawn scene, or your own photo toured by the camera, has no picture to make again: Rerun redoes its narration, and is offered only when it has some.",
          "A rerun is charged like the first run, and the button says about what it costs. It counts against the same project cap.",
        ],
      },
      {
        heading: "Editor",
        body: ["The Editor opens when the first render lands. It shows the video, a timeline, and for the selected shot its picture, its words and its motion. Rerun there makes a new version of that shot."],
      },
      {
        heading: "Download, Schedule and Post now",
        body: [
          "When the video is ready, Download saves the file.",
          "Schedule and Post now open one dialog: what it says, which of the accounts connected under Business → Channels it goes to, and, for Schedule, when. Post now means about a minute, and you can take it back until then. When nothing is connected the dialog says so and points at Business → Channels.",
        ],
      },
    ],
    notYet: [
      "Posting to a platform this box has no keys for. Its row under Business → Channels says not on this box yet; download the file and post it yourself.",
      "Trimming, reordering and cutting in the Editor.",
      "Asking for changes in the chat. Once Generate is pressed there is no chat box; change a shot with Rerun.",
    ],
    related: ["studio-plan", "channels", "credits", "not-yet"],
  },
  {
    slug: "content",
    title: "Content",
    where: "tab:Content",
    summary: "Everything you have made, and the photos and clips it is made of.",
    sections: [
      {
        heading: "What it is today",
        body: [
          "Content opens on Made: every finished post and video, newest first, playable where it sits, with what it cost, Post, Open and Download. A video made somewhere else can be imported there, for nothing, and posted like one made here.",
          "Photos & clips holds everything you have uploaded or made. Music, caption styles, overlays and actions show the shape of what they will hold, and store nothing yet.",
          "Your photos are also on the Studio's photo step: the ones you uploaded, the ones from your website, and pictures from finished posts.",
        ],
      },
    ],
    notYet: ["Music, caption styles, overlays or actions stored in Content, picked from it in the chat, or dropped from it on a timeline."],
    related: ["studio", "not-yet"],
  },
  {
    slug: "calendar",
    title: "Calendar",
    where: "tab:Calendar",
    summary: "What is scheduled, by month, week or list: a card per post per channel, coloured by what happened to it.",
    sections: [
      {
        heading: "What it is today",
        body: [
          "Calendar shows this month with today outlined, and Month, Week and List views. A scheduled post is a card on its day, one per channel, coloured by what happened to it: scheduled, published or failed.",
          "A post is scheduled from the Studio, once it is made: Schedule or Post now on the finished post opens the dialog that asks what it says, where it goes and when. A day with nothing on it is a day nothing was scheduled for.",
          "A day of your week that is not made yet is a dashed card with Make it, which opens it in the Studio. Prepare my week, in the Studio, is where a week is planned.",
        ],
      },
    ],
    notYet: ["Writing your own plan on a day, or dragging a finished post onto one."],
    related: ["studio-making", "channels", "not-yet"],
  },
  {
    slug: "analytics",
    title: "Analytics",
    where: "tab:Analytics",
    summary: "Who came to your website, who sent them, what they read, who got in touch, and what to make next.",
    sections: [
      {
        heading: "What it counts",
        body: [
          "Analytics counts your own website, once the line from Business, under Website & analytics, is in it: the people who came, the pages they read, who sent them, and who got in touch. Choose 7 days, 30 days or 90 days at the top.",
          "Getting in touch is a press on your phone number, email or WhatsApp link, which is found by itself, or a press on a button, or the sending of a form, that your website marks with data-joogoos-goal and a short name of your own, such as data-joogoos-goal=\"book\". The name is written in small letters, digits, dashes or underscores, starting with a letter. Only the name is sent, never anything somebody typed into the form.",
          "Below the first screen: where they were, what they were on, campaigns from links tagged with utm_campaign, and which website sent them.",
          "Your letters counts the people a letter to a new customer sent to your website, by the kind of customer it was written for under Who we look for. A link to your own website in the offer or the ask carries a short tag for that kind of customer, so a click is counted for the letters, never for the person who clicked.",
        ],
      },
      {
        heading: "Why it shows nothing",
        body: [
          "Until the line is in your website, or before anybody has visited since it went in, the tab says so and there is nothing to count. It fills in by itself when the first person arrives; there is nothing to press here.",
          "Only the website on your Business page is counted, and only from the day the line went in.",
        ],
      },
      {
        heading: "Asking about some people",
        body: ["Press a row, a source, a page, a country or a device, to count only those people; the sentence at the top is rewritten for them. Show everyone lets go."],
      },
      {
        heading: "What to make next",
        body: ["Once a few people have read the same page, or somebody got in touch, the top of the tab says what to make next and why, with Make this, which starts a post in the Studio with the idea written in. Nothing is spent until you press Generate there."],
      },
      {
        heading: "Nobody is followed",
        body: ["No cookie is set and nothing is stored in your visitors' browsers. A person is counted for one day and cannot be recognised the next, so somebody who comes back on three days of a week is three of the people counted that week, and there is no list of individual visitors."],
      },
    ],
    notYet: [
      "How your posts did on TikTok, LinkedIn and X. Instagram and Facebook are read each night, likes, comments and followers, and what a network does not give shows as a dash.",
      "Google reviews.",
      "Search Console's numbers, which are on the SEO tab rather than here.",
      "What happens after somebody gets in touch, such as a sign-up or an order in your own system.",
    ],
    related: ["credits", "channels", "not-yet"],
  },
  {
    slug: "seo",
    title: "SEO",
    where: "tab:SEO",
    summary: "Blog articles written from your own sources, approved by you, published on your own website.",
    sections: [
      {
        heading: "Why it is on your domain",
        body: [
          "Articles are published on your own website, not on ours, so the search credit is yours. Joogoos serves your published articles as data your site reads when it builds, and calls the build hook you paste in. It never gets a key to your code, your repository or your hosting.",
        ],
      },
      {
        heading: "Which website they go on",
        body: [
          "Your website address is not asked for here. You give it once, on your Business page under Channels, with everywhere else your business is — it is the same address Joogoos counts visits to, and your articles are published under it. Your website, on this tab, shows it and takes you there to change it.",
          "Change it on the Business page and your articles move with it, because there is only ever one address. What is on this tab is where they hang off it: the blog path, your languages, and the page every article's last link points at.",
        ],
      },
      {
        heading: "How an article happens",
        body: ["Four steps, and you are in three of them."],
        list: [
          "Plan topics proposes up to ten, from your business page and your sources. It costs, and the price is on the button.",
          "You accept, edit or drop each one, for nothing. Accepting is what decides; writing is what costs.",
          "Write produces a draft in which every factual sentence cites a source. You read it, resolve anything flagged, and press Approve.",
          "Publish sends every approved language at once and asks your site to rebuild.",
        ],
      },
      {
        heading: "Why a claim gets flagged",
        body: [
          "The writer is given your sources and nothing else, and it has to cite one for every fact. Before you see a draft, code checks it: a citation to a source it was not given is dropped, a number that is in no source it cites is flagged, a name on your never-name list is flagged and cannot be kept, and a link to anywhere you did not cite is removed.",
          "A flagged claim has three ways out: remove it, cite a source, or say you stand behind it — which records you as the source for that sentence. Approve is not possible until none is left. A name you never mention has only the first.",
          "What code cannot do is tell an honest sentence from a hint at a competitor. That part is yours, and it is why nothing is published without you pressing Approve.",
        ],
      },
      {
        heading: "Languages",
        body: [
          "Each language is its own edition with its own address, translated from the approved default-language version, and approved on its own. A language you have not published is never linked from the others. When the default language gets a newer approved version, the translations say they are behind and stay live until you translate again.",
        ],
      },
      {
        heading: "Knowing whether it worked",
        body: [
          "The SEO tab has two halves. Articles is where they are written; What Google says is where you find out whether any of it landed.",
          "Connect Google Search Console and Joogoos reads what people actually typed to find your site, which of your articles they saw, and where you come. It asks to read and nothing else: it cannot change your site, your verification or your sitemap, and you can take the permission back from your Google account at any time.",
          "Worth writing next is the list to trust. Those are phrases Google already shows your site for, on page two or three, that almost nobody clicks — you are being shown and passed over, so the work is half done already.",
          "Every number covers 28 days ending a few days ago, because Google publishes behind. A new article shows nothing for a week or two while Google finds it and decides where it belongs.",
        ],
      },
      {
        heading: "Before you pay to write",
        body: [
          "Check the search results on a topic costs a fraction of a penny and shows who already ranks for that phrase and what people ask next. Those questions go to the writer and usually become the article's FAQ.",
          "The list of who ranks is for you and is never given to the writer: those are your competitors, and a page of search results is exactly where their names would end up in your article.",
          "There is no search volume here. Nobody sells that at this price, and a made-up number would be worse than none.",
        ],
      },
      {
        heading: "Live, and not yet live",
        body: [
          "After publishing, Joogoos fetches the article's address twice to see whether it appeared. Until it answers, the tab says Sent rather than Live. If your site's build is broken, Joogoos will say so and cannot fix it.",
        ],
      },
    ],
    notYet: [
      "Publishing to WordPress, Shopify or Wix.",
      "How many people search for a phrase, and how a position has moved over months.",
      "Pictures inside an article, or scheduling one to publish later.",
      "More than one website per workspace.",
      "How an article ranks — that is Analytics, later.",
    ],
    related: ["business", "credits", "not-yet"],
  },
  {
    slug: "customers",
    title: "Customers",
    where: "tab:Customers",
    summary: "Finding your next customer: companies found or brought on a list, read and judged, and letters from your own mailbox that wait for your yes.",
    sections: [
      {
        heading: "Three screens",
        body: [
          "Today is who is waiting for you, in that order: somebody who answered, letters to approve, a decision only you can make, and one line on what Joogoos has been doing. Companies is the whole list, with a search box; a row opens that company. Set up is one checklist: the mailbox letters leave from, who to look for, what makes a company worth writing to, what the letter says, and how much Joogoos may do on its own.",
          "A company is found or brought, then read on its own website and judged against your own table of what makes a company worth writing to. Where it is worth it, a letter is written for it. Nothing is sent until the owner presses Send.",
        ],
      },
      {
        heading: "A mailbox kept for letters to new customers",
        body: [
          "Letters leave from a mailbox of yours, as you. Our advice is to make one kept only for them: an address used for nothing else, on a second domain of yours if you have one, and to connect that one on Business under Customers. Joogoos needs nothing more than the one button either way.",
          "A provider watches a mailbox that writes to strangers more closely than one that writes to friends. If it ever limits that mailbox, your everyday email keeps working. The answers to your letters are easy to find in it, and nothing else is in it.",
        ],
      },
      {
        heading: "A letter for each kind of customer",
        body: [
          "What the letter says is on Set up: your offer, your ask, and the language it is written in. A group under Who we look for can have its own letter as well, so a business that sells to two kinds of customer, or in two countries, says something different to each. Press Edit on the group and fill in Its own letter.",
          "Every field you leave empty there is Set up's, and the empty box shows Set up's words so you can see what will be sent. Switching what a letter says to one kind of customer is editing that one group. A letter already written keeps the words it was written with.",
        ],
      },
      {
        heading: "Import a list",
        body: [
          "On Companies, Import a list takes a CSV of named people at companies, the way a contact directory exports it. Choose the file, say where the list comes from, choose who we look for, and press Read the list. Reading adds nothing and costs nothing.",
          "It says what it read each column as, and names every column it did not read. Columns are read by their names, in English, French, Spanish and Arabic, never by where they sit, so a list with no column it can read as the company, or none it can read as an email address, is refused with the names to give them. A file saved from Excel with semicolons is read as well as one with commas.",
          "It then says how many people would be added, and why each of the others would not, before anything is added. Only the first person at each company is kept, because one letter goes to each company.",
          "Add puts them on your list. Each company is read on its own website and judged exactly as a company Joogoos found, so the judge can still say no to one you chose. For each one worth writing to, the letter is written at once and waits on Today, and on the Overview under Waiting for your yes. Importing is free; reading each company and writing its letter are charged as they always are, and the preview says about how much a company.",
        ],
        list: [
          "No email address, or one that is not an address.",
          "The list marks the address as not verified. When a list says whether its addresses are verified, only verified ones are kept, because a guessed address bounces.",
          "A free mailbox, such as gmail or outlook: a company's own address is the one to write to.",
          "An address you never write to again, because it asked you to stop, bounced, or was stopped by hand.",
          "A company or an address already on your list, or already earlier in the same file.",
          "No country, when you chose none for the rows that do not say.",
        ],
      },
      {
        heading: "A letter to a named person",
        body: [
          "A letter to somebody from a list greets them by their first name, in the letter's language. Everything else is the letter as it always is: one opening line written from what was read on their company's website, then your offer and your ask in your own words, and your name.",
          "The footer, which nobody edits, says why this address was written to, and it has to be true. For an address from a list it says the address is listed for that company in the source you named, a business contact directory, and only about their business, followed by how to stop. For a company Joogoos found, it says the company published the address itself.",
          "The address on the list is the one written to, even when the company's own website shows another, such as info@.",
        ],
      },
      {
        heading: "When somebody answers",
        body: [
          "A mailbox connected to Joogoos can send but not read, so an answer lands in your own inbox like any other email. Where Joogoos has a reply address for your letters, Business under Customers says so: each letter carries it, an answer stops whatever was still to go to them, and the answer is sent on to your mailbox.",
          "Where Joogoos does not see answers, the ask is best a link rather than a request to reply: a link to book a call, or to try what you offer, on your own website. A click there is counted on Analytics, and a reply is counted only when you say so. Set up says this under the ask when it has no link.",
          "Otherwise, open the company on Customers and press They replied. Nothing more goes to them, and the company shows on Today and on the Overview until you say how it went: a call is booked, not now, or they said no. A reply you mark is counted in your marketing review like any other.",
        ],
      },
    ],
    notYet: [
      "Reading your own mailbox for answers. An answer is seen through Joogoos's reply address where there is one, and otherwise when you press They replied.",
      "Noticing a letter that bounced: the bounce goes back to your own mailbox, where Joogoos does not look.",
    ],
    related: ["business", "overview", "credits"],
  },
  {
    slug: "you",
    title: "You and signing in",
    where: "panel:you",
    summary: "Who is signed in, and how signing in works.",
    sections: [
      {
        heading: "You",
        body: ["The You panel shows your name and your email. Signing in is a link by email, so there is no password to change."],
      },
      {
        heading: "Signing in",
        body: [
          "A sign-in link works once and expires in 15 minutes. After 5 links to one address in 15 minutes, no more are sent for a while.",
          "You stay signed in for 7 days, and that is renewed when you come back.",
          "Sign out is at the bottom of the account menu and of this dialog.",
        ],
      },
    ],
    notYet: [
      "Changing your name or email.",
    ],
    related: ["getting-started", "workspaces"],
  },
  {
    slug: "workspaces",
    title: "Workspaces, export and closing",
    where: "panel:workspace",
    summary: "One workspace per business, taking everything with you, and closing a workspace.",
    sections: [
      {
        heading: "Workspaces",
        body: [
          "A workspace is one business. What the Studio knows about it lives in its Business tab.",
          "One person can have 3 open workspaces. New workspace asks for the business's name, and Switch moves you to another. Both reload the app.",
          "Every workspace you have spends the same credit, which is yours. A new workspace brings no credit of its own.",
          "Rename changes the name on a workspace, in the account menu or in this panel. It is only the label: what the Studio writes from is the Business tab, and renaming a workspace changes nothing in it.",
        ],
      },
      {
        heading: "Take everything with you",
        body: [
          "Export makes a zip of the workspace: the Business page, each project's brief and script with its latest video, and every photo you uploaded. It is made in the background, and you get an email when it is ready or if it fails.",
          "An export is ready for 7 days. A large one comes in parts of up to 2 GB, each with its own Download.",
          "One workspace can ask for 3 exports a day, one at a time.",
        ],
      },
      {
        heading: "Closing a workspace",
        body: [
          "Delete, on a workspace's row, asks twice: first it says what happens, then it asks you to type that workspace's name. Any of your workspaces can be closed this way, not only the one you are in. In the account menu, Delete brings you here to ask.",
          "The workspace is locked at once, for you and for anyone else in it. Thirty days later everything in it is deleted for good: its projects, videos and photos, and the Business page.",
          "Your credit stays yours, and your other workspaces go on spending it. Closing refunds nothing, because the credit was never the workspace's.",
          "If an export is being made, wait for its email before closing: its link stops working once the workspace is closed.",
          "Export is of the workspace you are in. To take another one's files with you, switch to it first.",
        ],
      },
    ],
    notYet: [
      "Pictures from your website and generated pictures are not in the export. The videos made from them are.",
      "Inviting someone else into a workspace.",
      "Closing your whole account, rather than one workspace.",
    ],
    related: ["credits", "business", "you"],
  },
  {
    slug: "credits",
    title: "Credits and what costs money",
    where: "panel:credits",
    summary: "Credits are dollars, they are yours, and every call that spends them is listed.",
    sections: [
      {
        heading: "What credit is",
        body: [
          "Credits are dollars. They belong to you, not to a workspace: every workspace you have spends from one balance, which is shown at the top of every screen, beside the name of the workspace you are in.",
          "There is no free credit and no trial: an account starts at $0, and nothing that costs money runs until credit is added. There is no subscription either — you buy credit when you want it, and it is spent only on what you make.",
          "Credit lasts a year from the day it is added, and the oldest is spent first. When some will expire within two months, the Credits panel says how much.",
        ],
      },
      {
        heading: "What costs money",
        body: ["Every call Joogoos makes to create something for you is charged per call, and listed in the Credits panel. Generate and a rerun say about what they cost before you press them; the Business lookup and a photo upload do not yet. These make calls:"],
        list: [
          "The Business lookup, which runs when you answer the first question.",
          "Uploading a photo, which is described once so the script can use it.",
          "Everything Generate starts: the script, the pictures, the clips, the narration and the render.",
          "Rerunning a shot.",
        ],
      },
      {
        heading: "What does not",
        body: [],
        list: [
          "Signing in, answering questions, and editing the Business page yourself.",
          "Choosing photos and voices, and reading the plan.",
          "Downloading a finished video, and exporting a workspace.",
          "Filing a report, and asking Help.",
        ],
      },
      {
        heading: "Where you see a price first",
        body: ["The Studio's plan shows a price for each step, and a total, before Generate. The Business lookup, describing an uploaded photo and rerunning a shot are charged without a price on screen first."],
      },
      {
        heading: "Buying more",
        body: [
          "Top up buys credit in $10, $25 or $50. Pressing one takes you to Stripe's own payment page: your card details are typed there, not into Joogoos, and Joogoos never sees or keeps them. When you come back, your balance is updated as soon as the payment clears.",
          "$10 is the smallest, because below that the card fee takes about a tenth of it.",
        ],
      },
      {
        heading: "Your limits",
        body: [
          "Two ceilings, and both are yours to change. A day: nothing runs past this in one day, across all your workspaces — $50 to begin with. A month: a ceiling on a calendar month, which starts off unset.",
          "A step a limit refuses is not run and costs nothing. When one is in the way the Studio says which, and raising it is one field and one press; nothing you have made is lost, and everything free — trimming, reordering, writing your own words, playing the whole thing in the browser — goes on working while you decide.",
        ],
      },
      {
        heading: "The lists",
        body: [
          "What you have bought lists each lot of credit: when it was added, how much, what is left and when it expires. Every movement lists the latest twelve changes to your balance, with the workspace each spend was made in.",
          "Every call it has paid for lists each call: when, what it was, which service made it, whether it worked, what it read and wrote, how long it took, and what it cost. Held means money set aside while the call runs. Everything, Running and Failed filter the list.",
          "Above the list, the latest refusals say why something was not run: there was not enough credit, your daily or monthly limit was reached, the post reached its own cap, it was tried three times and stopped, or it took too long and was stopped.",
        ],
      },
    ],
    notYet: [
      "Card payments on a server that has not been set up for them: the packs are drawn and say so rather than failing when pressed.",
      "A refund. Credit that has been bought cannot be turned back into money, and closing a workspace refunds nothing — the credit stays yours and your other workspaces can spend it.",
      "A call a limit refused is not a row in the list. Only the latest few refusals, for this workspace, are shown above it.",
    ],
    related: ["studio-plan", "business", "workspaces", "glossary"],
  },
  {
    slug: "settings",
    title: "Settings",
    where: "panel:settings",
    summary: "Which emails you get. The switches cannot be changed yet, and where posts go is under Business → Channels.",
    sections: [
      {
        heading: "Where posts go",
        body: ["Not here. The accounts your posts go out on are connected under Business → Channels, with the rest of where your business is, and the Channels help page says how."],
      },
      {
        heading: "What we email you about",
        body: [
          "The switches for A video is ready, Credit is running low and A publish failed do not change anything, and none of those three emails is sent today.",
          "The emails Joogoos does send are the sign-in link, an export being ready or failing, and the emails about your reports.",
        ],
      },
    ],
    notYet: ["Choosing which emails you get."],
    related: ["channels", "not-yet", "reports"],
  },
  {
    slug: "reports",
    title: "Reports",
    where: "panel:reports",
    summary: "Asking Help, telling us something is broken, missing or unclear, and following what happens to it.",
    sections: [
      {
        heading: "Asking Help",
        body: [
          "Help is under your picture, in Settings, on this page: Ask a question, or report a problem. Ask it how something in Joogoos works, and the answer comes from these help pages and names the page it came from. One conversation holds up to 6 questions, and one person can ask 20 a day.",
          "A question about one of your own charges or reports is sent to Credits or to Your reports, where they are. Something broken or missing becomes a report Help writes for you, which you read, change and send yourself.",
          "Help cannot see your projects, your business page, your credit or your reports, and it cannot change or run anything. Close it and the conversation is gone.",
          "Where asking is not switched on, Help opens the report form straight away.",
        ],
      },
      {
        heading: "Filing a report",
        body: [
          "In Help, Write a report instead opens the form. Choose Something is broken, Something should exist or I do not understand this, write it in a line and say what happened, then press Send.",
          "The tab you are on, the project open in the Studio and the version of Joogoos go with it, so you need not describe where you were. If a screen showed you a reference in the last hour, it is filled in for you.",
          "The dialog also links to the help page for the screen you are on.",
        ],
      },
      {
        heading: "Your reports",
        body: [
          "Your reports lists everything this workspace has reported, newest first, each with a number such as R-12.",
          "Each has a status: open, looking at it, fixed, closed, not a bug or won't do. A fixed report says which version fixed it.",
          "Open one to read its thread, reply, or close it. You get an email each time something happens to it.",
        ],
      },
    ],
    related: ["getting-started", "whats-new"],
  },
  {
    slug: "whats-new",
    title: "What's new",
    where: "panel:new",
    summary: "What changed in each release of Joogoos.",
    sections: [
      {
        heading: "Reading it",
        body: ["What's new lists the latest ten versions, each with what is new and what was fixed. Until a release has something new in it, the panel says there is nothing to show yet."],
      },
    ],
    related: ["reports"],
  },
  {
    slug: "not-yet",
    title: "What Joogoos does not do yet",
    where: null,
    summary: "Everything a screen shows or mentions that does not work yet, in one place.",
    sections: [
      {
        heading: "Posting",
        body: [],
        list: [
          "Connecting a platform this box has no keys for: its row under Business → Channels says not on this box yet. Download the video and post it yourself.",
          "Writing your own plan on a Calendar day.",
          "Music, caption styles and overlays in Content, which show their shape and store nothing.",
          "How a post did on each network, in Analytics: views, likes and followers.",
        ],
      },
      {
        heading: "Money",
        body: [],
        list: [
          "A refund. Credit that has been bought cannot be turned back into money.",
          "Buying credit on a server that has not been set up for card payments: the packs are drawn and say so.",
          "Seeing a price before the Business lookup or a photo upload.",
        ],
      },
      {
        heading: "The Studio",
        body: [],
        list: [
          "Hearing a voice before choosing it.",
          "Finding pictures for you.",
          "Attach, Photos and Dictate, under the chat box.",
          "Changing a finished video by typing in the chat.",
          "Trimming and reordering in the Editor.",
          "Rerunning anything but a shot.",
        ],
      },
      {
        heading: "Your account",
        body: [],
        list: [
          "The emails about a video being ready, credit running low or a publish failing.",
          "Inviting someone into a workspace.",
          "Closing your whole account.",
          "Using Joogoos in another language. The screens, these help pages and the emails are in English; only the Business and Studio chats answer in the language you write in.",
        ],
      },
      {
        heading: "Help",
        body: ["Where asking is not switched on, Help is the report form: choose I do not understand this, and a person answers your question."],
      },
    ],
    related: ["channels", "settings", "credits", "studio"],
  },
  {
    slug: "glossary",
    title: "Glossary",
    where: null,
    summary: "The words Joogoos uses on its screens, and what each one means here.",
    sections: GLOSSARY.map((g) => ({ heading: g.term, body: [g.definition] })),
    related: ["getting-started", "credits"],
  },
];
